import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  GalleryDto,
  GalleryGroupAccessLevel,
  GalleryPublicAccessLevel,
  GalleryTenantAccessLevel,
  TenantDto,
} from '@nusign/picflow-backend-types';
import { combineLatest, filter, from, map, of, shareReplay, switchMap } from 'rxjs';
import { GalleryAccessLevelService } from '../../../services/gallery-access-level.service';
import { GalleryService } from '../../../services/gallery.service';
import { OrganizationService } from '../../../services/organization.service';
import { StateService } from '../../../services/state.service';
import { UserGroupService } from '../../../services/user-group.service';
import { GalleryShareContextComponent } from '../../../shared-extra/components/gallery-share-context/gallery-share-context.component';
import { RadioButtonComponent } from '../../../shared-extra/components/radio-button/radio-button.component';
import { BillingScopePipe } from '../../../shared-extra/pipes/billing-scope.pipe';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { AccessItemBodyComponent } from '../access-item-body/access-item-body.component';
import { AccessItemCtaComponent } from '../access-item-cta/access-item-cta.component';
import { AccessItemHeaderComponent } from '../access-item-header/access-item-header.component';
import { AccessItemComponent } from '../access-item/access-item.component';

@Component({
  selector: 'app-gallery-team-access-item',
  imports: [
    GalleryShareContextComponent,
    AccessItemComponent,
    AccessItemHeaderComponent,
    AccessItemBodyComponent,
    AccessItemCtaComponent,
    ContextMenuComponent,
    RadioButtonComponent,
    AsyncPipe,
    SvgComponent,
    BillingScopePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './gallery-team-access-item.component.html',
  styleUrl: './gallery-team-access-item.component.scss',
})
export class GalleryTeamAccessItemComponent {
  private galleryService = inject(GalleryService);
  private accessLevelService = inject(GalleryAccessLevelService);
  private state = inject(StateService);
  private organizationService = inject(OrganizationService);
  private userGroupService = inject(UserGroupService);
  gallery = input.required<GalleryDto>();
  galleryPermissions = input.required<GalleryDto['permissions']>();
  tenantAccessLevels: GalleryTenantAccessLevel[] = ['edit', 'review', 'view_only', 'no_access'];
  tenantAccessLevel = toSignal(
    toObservable(this.gallery).pipe(
      switchMap(gallery => {
        if (gallery.tenant_access) return of(gallery.tenant_access);
        if (gallery.tenant_access_effective) return of(gallery.tenant_access_effective);
        return from(this.accessLevelService.getAccessLevels(gallery)).pipe(map(levels => levels[0]));
      })
    )
  );

  allUserGroups$ = this.userGroupService.getUserGroups();

  groupUserCountMap$ = this.userGroupService.getUserGroups().pipe(
    switchMap(userGroups =>
      combineLatest(
        userGroups.map(group =>
          this.userGroupService
            .getUserGroupUsers(group.id)
            .pipe(map(users => ({ id: group.id, userCount: users.length })))
        )
      )
    ),
    map(data => {
      return data.reduce(
        (acc, { id, userCount }) => {
          acc[id] = userCount;
          return acc;
        },
        {} as { [key: string]: number }
      );
    }),
    shareReplay(1)
  );

  groupUsersEnabledMap$ = combineLatest([this.userGroupService.getUserGroups(), toObservable(this.gallery)]).pipe(
    switchMap(([userGroups, gallery]) =>
      this.userGroupService.getGalleryUserGroups(gallery.id).pipe(
        map(galleryGroups =>
          userGroups.reduce(
            (acc, group) => {
              acc[group.id] = galleryGroups.some(g => g.group === group.id);
              return acc;
            },
            {} as { [key: string]: boolean }
          )
        )
      )
    ),
    shareReplay(1)
  );

  tenant = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    if (typeof gallery.tenant === 'object') return gallery.tenant as TenantDto;
    return (
      this.state.tenants?.find(t => t.id === gallery.tenant) ||
      this.state.sharedWithMeTenants.find(t => t.id === gallery.tenant)
    );
  });

  isTeamMember = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    const tenantId = (gallery.tenant as TenantDto)?.id || (gallery.tenant as string);
    return this.state.tenants?.some(t => t.id === tenantId);
  });

  teamSize = toSignal(
    toObservable(this.isTeamMember).pipe(
      filter(Boolean),
      switchMap(() => this.organizationService.getMembers()),
      map(members => members.length)
    )
  );

  iconColor = computed(() => {
    const tenantAccessLevel = this.tenantAccessLevel();
    if (!tenantAccessLevel) return;
    if (tenantAccessLevel === 'no_access') return 'red';
    return 'primary';
  });

  changeTeamAccessLevel(accessLevel: GalleryPublicAccessLevel) {
    this.galleryService.updateGallery(this.gallery().id, {
      tenant_access: accessLevel,
    });
  }

  toggleUserGroup(groupId: string, checked: boolean) {
    if (checked) {
      const accessLevel =
        this.gallery().tenant_access === 'no_access'
          ? 'view_only'
          : (this.gallery().tenant_access as GalleryGroupAccessLevel);
      this.userGroupService.addUserGroupToGallery(groupId, this.gallery().id, accessLevel).subscribe();
    } else {
      this.userGroupService.removeUserGroupFromGallery(groupId, this.gallery().id).subscribe();
    }
  }
}
